import { LockIcon, PlayIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { PaywallDialog } from "@/components/PaywallDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { trackEvent } from "@/lib/analytics";
import { FREE_REVIEW_NOTE_DAYS, ReportTab, TestPhase } from "@/lib/constant";
import { isNativeApp, nativeBridge } from "@/lib/native";
import { useQuizContext } from "@/router/test/_hooks/QuizContext";

import ReportViewHeader from "./ReportViewHeader";
import ReportViewTab from "./ReportViewTab";
import { WordCard } from "./WordCard";

export function ReportView() {
  const { results, phase, noteOnlyCorrect, isReviewNoteLocked, setReportTab, toggleNoteFilter, exitToList, speakWord } =
    useQuizContext();
  const tab = phase.type === TestPhase.REPORT ? phase.tab : ReportTab.CARD;
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [isUnlockedByAd, setIsUnlockedByAd] = useState(false);
  const [isLoadingRewarded, setIsLoadingRewarded] = useState(false);

  const noteResults = noteOnlyCorrect
    ? results.filter(r => r.isCorrect)
    : results.filter(r => !r.isCorrect || r.selectedOptionIndex === null);
  const displayedResults = tab === ReportTab.CARD ? results : noteResults;

  const showLockedOverlay = tab === ReportTab.NOTE && isReviewNoteLocked && !isUnlockedByAd;

  function handleUpgradeClick() {
    trackEvent("paywall_triggered", { source: "review_note" });
    setPaywallOpen(true);
  }

  const handleWatchAd = useCallback(async () => {
    setIsLoadingRewarded(true);
    trackEvent("ad_rewarded_initiated", { source: "review_note_unlock" });

    try {
      const result = await nativeBridge.ad.showRewarded();

      if (result.rewarded) {
        setIsUnlockedByAd(true);
        trackEvent("ad_rewarded_completed", { source: "review_note_unlock" });
        toast.success("Review Note가 열렸어요!", { position: "top-center" });
      }
    } finally {
      setIsLoadingRewarded(false);
    }
  }, []);

  return (
    <Card className="flex h-full min-h-0 flex-col overflow-hidden bg-primary-background pt-2 pb-0 sm:mx-auto sm:my-8 sm:h-[calc(100dvh-120px)] sm:max-w-lg sm:rounded-2xl sm:bg-primary-background sm:pt-5 sm:shadow-lg sm:min-w-lg">
      {/* 리포트 헤더 */}
      <ReportViewHeader
        onBack={exitToList}
        tab={tab}
        noteOnlyCorrect={noteOnlyCorrect}
        onToggleFilter={showLockedOverlay ? undefined : toggleNoteFilter}
      />

      {/* 리포트 본문 */}
      <CardContent className="flex flex-1 min-h-0 flex-col gap-4 overflow-hidden px-5 scrollbar-hidden sm:px-4">
        {/* 리포트 탭 */}
        <ReportViewTab tab={tab} onTabChange={setReportTab} />

        {/* 리포트 카드 목록 */}
        <div className="relative flex-1 min-h-0 overflow-y-auto overscroll-none space-y-3 pb-16 scrollbar-hidden">
          {/* 무료 유저 3일 초과 잠금 오버레이 */}
          {showLockedOverlay && (
            <div className="sticky inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-primary-background/80 backdrop-blur-sm rounded-xl py-12 px-6">
              <div className="flex items-center justify-center size-12 rounded-full bg-muted">
                <LockIcon className="size-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-center text-foreground font-medium leading-relaxed">
                최근 {FREE_REVIEW_NOTE_DAYS}일 이내 테스트만
                <br />
                Review Note를 볼 수 있어요
              </p>

              <div className="flex flex-col items-center gap-2 w-full max-w-52">
                <Button
                  type="button"
                  variant="default"
                  size="lg"
                  className="w-full h-auto py-2.5 px-5"
                  onClick={handleUpgradeClick}
                >
                  Plus로 전체 기록 보기
                </Button>

                {/* 보상형 광고 버튼 (네이티브 앱 전용) */}
                {isNativeApp && (
                  <Button
                    type="button"
                    size="lg"
                    variant="outline"
                    className="w-full h-auto gap-1.5 py-2.5 px-5"
                    disabled={isLoadingRewarded}
                    onClick={handleWatchAd}
                  >
                    <PlayIcon className="size-3.5" />
                    {isLoadingRewarded ? "광고 로딩 중..." : "광고 보고 열기"}
                  </Button>
                )}
              </div>
            </div>
          )}

          {!showLockedOverlay && (
            <>
              {displayedResults.map((result, idx) => (
                <WordCard
                  key={result.question.id}
                  number={idx + 1}
                  result={result}
                  onSpeak={() => speakWord(result.question.word)}
                />
              ))}

              {/* 리포트 카드 목록이 없을 때 */}
              {tab === ReportTab.NOTE && displayedResults.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full">
                  <p className="text-sm text-center text-muted-foreground font-semibold">
                    {noteOnlyCorrect ? (
                      <>
                        맞춘 단어가 없어요 :(
                        <br />
                        지금까지 배운 단어를 다시 한번 확인해보세요 💪
                      </>
                    ) : (
                      <>
                        틀린 단어가 없어요 :)
                        <br />
                        다른 단어도 학습해보세요 🎉
                      </>
                    )}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>

      <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} source="review_note" />
    </Card>
  );
}
